/**
 * ActivityLogPage.jsx
 * Admin page to view, filter, and export the User Activity Log.
 * Displays stats cards, a filterable/paginated table, and a CSV export button.
 * Uses the Glassmorphic dark-mode design system defined in UI_DESIGN_SYSTEM.md.
 */

import { useState, useEffect, useCallback } from 'react';
import AppLayout from '../../components/AppLayout';
import {
  getActivityLogs,
  getActivityStats,
  exportActivityLogs,
  cleanupOldLogs,
} from '../../api/activityLogService';

// ── Helpers ───────────────────────────────────────────────────────────────────

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
  }) + ', ' + d.toLocaleTimeString('en-IN', {
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  });
};

const getDefaultStartDate = () => {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return d.toISOString().split('T')[0];
};

const getDefaultEndDate = () => new Date().toISOString().split('T')[0];

const parseUserAgent = (ua = '') => {
  if (!ua || ua === 'unknown') return 'Unknown Device';
  if (/Mobile|Android/i.test(ua)) {
    if (/iPhone|iPad/i.test(ua)) return 'iOS Mobile';
    return 'Android Mobile';
  }
  if (/Chrome/i.test(ua)) return 'Chrome / Desktop';
  if (/Firefox/i.test(ua)) return 'Firefox / Desktop';
  if (/Safari/i.test(ua)) return 'Safari / Desktop';
  if (/Edge/i.test(ua)) return 'Edge / Desktop';
  return ua.substring(0, 40) + (ua.length > 40 ? '…' : '');
};

const downloadCsv = (logs) => {
  const headers = ['Timestamp', 'Email', 'Action', 'Status', 'IP Address', 'Device', 'Metadata'];
  const rows = logs.map((l) => [
    formatDate(l.createdAt),
    l.email,
    l.action,
    l.status,
    l.ipAddress,
    parseUserAgent(l.userAgent),
    JSON.stringify(l.metadata || {}),
  ]);
  const csvContent = [headers, ...rows]
    .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
    .join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `activity-logs-${new Date().toISOString().split('T')[0]}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

// ── Badge configs ─────────────────────────────────────────────────────────────

const ACTION_STYLES = {
  LOGIN:            { label: 'LOGIN',            bg: 'rgba(0,242,96,0.15)',   color: '#00f260', border: 'rgba(0,242,96,0.3)' },
  LOGOUT:           { label: 'LOGOUT',           bg: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.6)', border: 'rgba(255,255,255,0.15)' },
  FAILED_LOGIN:     { label: 'FAILED LOGIN',     bg: 'rgba(255,107,107,0.15)', color: '#ff6b6b', border: 'rgba(255,107,107,0.3)' },
  DATA_EXPORT:      { label: 'DATA EXPORT',      bg: 'rgba(79,172,254,0.15)', color: '#4facfe', border: 'rgba(79,172,254,0.3)' },
  ALERT_CREATED:    { label: 'ALERT CREATED',    bg: 'rgba(102,126,234,0.15)', color: '#667eea', border: 'rgba(102,126,234,0.3)' },
  ALERT_DELETED:    { label: 'ALERT DELETED',    bg: 'rgba(254,202,87,0.15)', color: '#feca57', border: 'rgba(254,202,87,0.3)' },
  PASSWORD_CHANGED: { label: 'PASSWORD CHANGED', bg: 'rgba(255,159,64,0.15)', color: '#ff9f40', border: 'rgba(255,159,64,0.3)' },
  PROFILE_UPDATED:  { label: 'PROFILE UPDATED',  bg: 'rgba(0,242,254,0.15)', color: '#00f2fe', border: 'rgba(0,242,254,0.3)' },
};

const ActionBadge = ({ action }) => {
  const style = ACTION_STYLES[action] || { label: action, bg: 'rgba(255,255,255,0.08)', color: '#fff', border: 'rgba(255,255,255,0.2)' };
  return (
    <span style={{
      background: style.bg,
      color: style.color,
      border: `1px solid ${style.border}`,
      borderRadius: 4,
      padding: '2px 8px',
      fontSize: '0.68rem',
      fontWeight: 700,
      letterSpacing: '0.04em',
      whiteSpace: 'nowrap',
    }}>
      {style.label}
    </span>
  );
};

const StatusDot = ({ status }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
    <span style={{
      width: 8, height: 8, borderRadius: '50%',
      background: status === 'success' ? '#00f260' : '#ff6b6b',
      boxShadow: `0 0 6px ${status === 'success' ? '#00f260' : '#ff6b6b'}`,
      display: 'inline-block',
    }} />
    <span style={{ color: status === 'success' ? '#00f260' : '#ff6b6b', fontSize: '0.8rem', fontWeight: 600 }}>
      {status === 'success' ? 'Success' : 'Failed'}
    </span>
  </span>
);

// ── Skeleton row ──────────────────────────────────────────────────────────────

const SkeletonRow = () => (
  <tr>
    {Array.from({ length: 7 }).map((_, i) => (
      <td key={i} style={{ padding: '0.75rem 1rem' }}>
        <div style={{
          height: 14,
          borderRadius: 6,
          background: 'linear-gradient(90deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.12) 50%, rgba(255,255,255,0.05) 100%)',
          backgroundSize: '600px 100%',
          animation: 'shimmer 1.5s infinite',
          width: `${50 + Math.random() * 40}%`,
        }} />
      </td>
    ))}
  </tr>
);

// ── Stat Card ─────────────────────────────────────────────────────────────────

const StatCard = ({ label, value, gradient, icon }) => (
  <div style={{
    background: 'rgba(255,255,255,0.05)',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 16,
    padding: '1.25rem 1.5rem',
    boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
  }}>
    <div style={{
      width: 48, height: 48, borderRadius: 12,
      background: gradient,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: '1.4rem', flexShrink: 0,
      boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
    }}>
      {icon}
    </div>
    <div>
      <div style={{ fontSize: '1.6rem', fontWeight: 700, color: '#fff', lineHeight: 1.1 }}>{value ?? '—'}</div>
      <div style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.6)', marginTop: 2 }}>{label}</div>
    </div>
  </div>
);

// ── Main Page ─────────────────────────────────────────────────────────────────

const ActivityLogPage = () => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 50, totalPages: 1 });
  const [expandedRow, setExpandedRow] = useState(null);
  const [cleanupMsg, setCleanupMsg] = useState('');

  const [filters, setFilters] = useState({
    action: '',
    status: '',
    email: '',
    startDate: getDefaultStartDate(),
    endDate: getDefaultEndDate(),
  });
  const [appliedFilters, setAppliedFilters] = useState({ ...filters });

  // ── Fetch logs ──────────────────────────────────────────────────────────────
  const fetchLogs = useCallback(async (page = 1, filterParams = appliedFilters) => {
    setLoading(true);
    try {
      const params = { ...filterParams, page, limit: 50 };
      // strip empty strings
      Object.keys(params).forEach((k) => { if (params[k] === '') delete params[k]; });
      const { data } = await getActivityLogs(params);
      setLogs(data.data);
      setPagination(data.pagination);
    } catch (err) {
      console.error('Failed to fetch logs:', err);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters]);

  // ── Fetch stats ─────────────────────────────────────────────────────────────
  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const { data } = await getActivityStats();
      setStats(data.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchLogs(1, appliedFilters);
  }, [appliedFilters]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleApplyFilter = () => {
    setAppliedFilters({ ...filters });
  };

  const handleReset = () => {
    const defaultFilters = {
      action: '',
      status: '',
      email: '',
      startDate: getDefaultStartDate(),
      endDate: getDefaultEndDate(),
    };
    setFilters(defaultFilters);
    setAppliedFilters(defaultFilters);
  };

  const handleExportCsv = async () => {
    setExporting(true);
    try {
      const params = { ...appliedFilters };
      Object.keys(params).forEach((k) => { if (params[k] === '') delete params[k]; });
      const { data } = await exportActivityLogs(params);
      downloadCsv(data.data);
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setExporting(false);
    }
  };

  const handleCleanup = async () => {
    if (!window.confirm('Delete all logs older than 90 days? This cannot be undone.')) return;
    try {
      const { data } = await cleanupOldLogs(90);
      setCleanupMsg(data.message);
      fetchLogs(1);
      fetchStats();
      setTimeout(() => setCleanupMsg(''), 5000);
    } catch (err) {
      console.error('Cleanup failed:', err);
    }
  };

  // ── Stat values ─────────────────────────────────────────────────────────────
  const s = stats?.today || {};
  const statCards = [
    { label: 'Logins Today',        value: s.LOGIN ?? 0,        gradient: 'linear-gradient(135deg,#00f260,#0575e6)', icon: '🔑' },
    { label: 'Failed Logins Today', value: s.FAILED_LOGIN ?? 0, gradient: 'linear-gradient(135deg,#ff6b6b,#ee5a6f)', icon: '🚫' },
    { label: 'Data Exports Today',  value: s.DATA_EXPORT ?? 0,  gradient: 'linear-gradient(135deg,#4facfe,#00f2fe)', icon: '📥' },
    { label: 'Active Users Today',  value: stats?.topActiveUsers?.length ?? 0, gradient: 'linear-gradient(135deg,#667eea,#764ba2)', icon: '👥' },
  ];

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <AppLayout pageBadge="Activity Log">
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg,#1a1a2e 0%,#16213e 100%)',
      color: '#fff',
      fontFamily: "-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif",
      padding: '2rem',
    }}>
      <style>{`
        @keyframes shimmer {
          0%   { background-position: -600px 0; }
          100% { background-position: 600px 0; }
        }
        .log-table-row:hover { background: rgba(102,126,234,0.08) !important; }
        .filter-input {
          background: rgba(255,255,255,0.06);
          border: 1px solid rgba(255,255,255,0.12);
          border-radius: 8px;
          color: #fff;
          padding: 0.5rem 0.75rem;
          font-size: 0.85rem;
          outline: none;
          transition: border-color 0.2s;
        }
        .filter-input:focus { border-color: rgba(102,126,234,0.6); }
        .filter-input option { background: #1a1a2e; }
        .page-btn {
          background: rgba(255,255,255,0.06);
          border: 1px solid rgba(255,255,255,0.12);
          color: rgba(255,255,255,0.8);
          border-radius: 6px;
          padding: 0.35rem 0.7rem;
          cursor: pointer;
          transition: all 0.2s;
          font-size: 0.82rem;
        }
        .page-btn:hover:not(:disabled) { background: rgba(255,255,255,0.12); color:#fff; }
        .page-btn:disabled { opacity:0.3; cursor:not-allowed; }
        .page-btn.active {
          background: linear-gradient(135deg,#667eea,#764ba2);
          border-color: transparent;
          color: #fff;
          font-weight: 600;
        }
        .action-btn {
          border: none;
          border-radius: 8px;
          padding: 0.55rem 1.2rem;
          font-weight: 600;
          font-size: 0.85rem;
          cursor: pointer;
          transition: all 0.25s;
        }
        .action-btn:hover { transform: translateY(-1px); }
      `}</style>

      {/* ── Header ── */}
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ fontSize: '1.8rem', fontWeight: 700, margin: 0 }}>
          📋 User Activity Log
        </h1>
        <p style={{ color: 'rgba(255,255,255,0.55)', marginTop: 4, fontSize: '0.9rem' }}>
          Track every user action — logins, exports, profile changes, and more.
        </p>
      </div>

      {/* ── Stats Cards ── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))',
        gap: '1rem',
        marginBottom: '1.5rem',
      }}>
        {statCards.map((card) => (
          <StatCard key={card.label} {...card} />
        ))}
      </div>

      {/* ── Filters ── */}
      <div style={{
        background: 'rgba(255,255,255,0.04)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 16,
        padding: '1.25rem 1.5rem',
        marginBottom: '1.5rem',
        boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
      }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'flex-end' }}>
          {/* Action */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Action</label>
            <select
              className="filter-input"
              value={filters.action}
              onChange={(e) => setFilters((f) => ({ ...f, action: e.target.value }))}
              style={{ minWidth: 160 }}
            >
              <option value="">All Actions</option>
              {Object.keys(ACTION_STYLES).map((a) => (
                <option key={a} value={a}>{ACTION_STYLES[a].label}</option>
              ))}
            </select>
          </div>

          {/* Status */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status</label>
            <select
              className="filter-input"
              value={filters.status}
              onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
              style={{ minWidth: 130 }}
            >
              <option value="">All</option>
              <option value="success">Success</option>
              <option value="failed">Failed</option>
            </select>
          </div>

          {/* Email */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>User Email</label>
            <input
              type="text"
              className="filter-input"
              placeholder="Search email..."
              value={filters.email}
              onChange={(e) => setFilters((f) => ({ ...f, email: e.target.value }))}
              style={{ minWidth: 200 }}
            />
          </div>

          {/* Start Date */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Start Date</label>
            <input
              type="date"
              className="filter-input"
              value={filters.startDate}
              onChange={(e) => setFilters((f) => ({ ...f, startDate: e.target.value }))}
            />
          </div>

          {/* End Date */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>End Date</label>
            <input
              type="date"
              className="filter-input"
              value={filters.endDate}
              onChange={(e) => setFilters((f) => ({ ...f, endDate: e.target.value }))}
            />
          </div>

          {/* Buttons */}
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <button
              className="action-btn"
              onClick={handleApplyFilter}
              style={{ background: 'linear-gradient(135deg,#667eea,#764ba2)', color: '#fff', boxShadow: '0 4px 15px rgba(102,126,234,0.3)' }}
            >
              Apply Filter
            </button>
            <button
              className="action-btn"
              onClick={handleReset}
              style={{ background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.8)', border: '1px solid rgba(255,255,255,0.12)' }}
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* ── Table Card ── */}
      <div style={{
        background: 'rgba(255,255,255,0.04)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 16,
        boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
        overflow: 'hidden',
      }}>
        {/* Table toolbar */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '1rem 1.5rem',
          borderBottom: '1px solid rgba(255,255,255,0.07)',
          flexWrap: 'wrap', gap: '0.5rem',
        }}>
          <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem' }}>
            {loading ? 'Loading…' : `${pagination.total.toLocaleString()} total records`}
          </span>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button
              className="action-btn"
              onClick={handleExportCsv}
              disabled={exporting}
              style={{
                background: 'linear-gradient(135deg,#00f260,#0575e6)',
                color: '#fff',
                boxShadow: '0 4px 15px rgba(0,242,96,0.25)',
                opacity: exporting ? 0.7 : 1,
              }}
            >
              {exporting ? '⏳ Exporting…' : '📥 Export CSV'}
            </button>
            <button
              className="action-btn"
              onClick={handleCleanup}
              style={{
                background: 'rgba(255,107,107,0.15)',
                color: '#ff6b6b',
                border: '1px solid rgba(255,107,107,0.3)',
              }}
            >
              🗑 Cleanup Old Logs
            </button>
          </div>
        </div>

        {cleanupMsg && (
          <div style={{ padding: '0.75rem 1.5rem', background: 'rgba(0,242,96,0.1)', color: '#00f260', fontSize: '0.85rem', borderBottom: '1px solid rgba(0,242,96,0.2)' }}>
            ✅ {cleanupMsg}
          </div>
        )}

        {/* Scrollable table */}
        <div style={{ overflowX: 'auto', maxHeight: 620, overflowY: 'auto' }}
          className="activity-log-scroll">
          <style>{`
            .activity-log-scroll::-webkit-scrollbar { width: 7px; height: 7px; }
            .activity-log-scroll::-webkit-scrollbar-track { background: rgba(255,255,255,0.04); }
            .activity-log-scroll::-webkit-scrollbar-thumb {
              background: linear-gradient(135deg,#667eea,#764ba2);
              border-radius: 4px;
            }
          `}</style>
          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, minWidth: 900 }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
              <tr style={{ background: 'rgba(15,15,30,0.96)', backdropFilter: 'blur(10px)' }}>
                {['Timestamp', 'User Email', 'Action', 'Status', 'IP Address', 'Device/Browser', 'Details'].map((h) => (
                  <th key={h} style={{
                    padding: '0.9rem 1rem',
                    textAlign: 'left',
                    fontSize: '0.78rem',
                    fontWeight: 700,
                    color: 'rgba(255,255,255,0.85)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '2px solid rgba(102,126,234,0.3)',
                    whiteSpace: 'nowrap',
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading
                ? Array.from({ length: 8 }).map((_, i) => <SkeletonRow key={i} />)
                : logs.length === 0
                  ? (
                    <tr>
                      <td colSpan={7} style={{ textAlign: 'center', padding: '4rem 1rem' }}>
                        <div style={{ fontSize: '3rem', marginBottom: '0.75rem' }}>📭</div>
                        <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.95rem' }}>No activity logs match your filters.</div>
                        <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.8rem', marginTop: 4 }}>Try adjusting the date range or clearing the filters.</div>
                      </td>
                    </tr>
                  )
                  : logs.map((log, idx) => (
                    <>
                      <tr
                        key={log._id}
                        className="log-table-row"
                        onClick={() => setExpandedRow(expandedRow === log._id ? null : log._id)}
                        style={{
                          cursor: 'pointer',
                          background: idx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)',
                          borderBottom: '1px solid rgba(255,255,255,0.04)',
                          transition: 'background 0.15s',
                        }}
                      >
                        <td style={{ padding: '0.7rem 1rem', fontSize: '0.8rem', color: 'rgba(255,255,255,0.65)', whiteSpace: 'nowrap' }}>
                          {formatDate(log.createdAt)}
                        </td>
                        <td style={{ padding: '0.7rem 1rem', fontSize: '0.83rem', color: '#fff', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {log.email}
                        </td>
                        <td style={{ padding: '0.7rem 1rem' }}>
                          <ActionBadge action={log.action} />
                        </td>
                        <td style={{ padding: '0.7rem 1rem' }}>
                          <StatusDot status={log.status} />
                        </td>
                        <td style={{ padding: '0.7rem 1rem', fontSize: '0.8rem', color: 'rgba(255,255,255,0.6)', fontFamily: 'monospace' }}>
                          {log.ipAddress}
                        </td>
                        <td style={{ padding: '0.7rem 1rem', fontSize: '0.8rem', color: 'rgba(255,255,255,0.55)', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {parseUserAgent(log.userAgent)}
                        </td>
                        <td style={{ padding: '0.7rem 1rem', fontSize: '0.78rem', color: 'rgba(255,255,255,0.45)' }}>
                          {Object.keys(log.metadata || {}).length > 0
                            ? <span style={{ color: 'rgba(102,126,234,0.9)' }}>▶ View</span>
                            : '—'}
                        </td>
                      </tr>
                      {expandedRow === log._id && (
                        <tr key={`${log._id}-expand`} style={{ background: 'rgba(102,126,234,0.06)' }}>
                          <td colSpan={7} style={{ padding: '0.6rem 1rem 0.6rem 3rem' }}>
                            <div style={{
                              background: 'rgba(0,0,0,0.3)',
                              borderRadius: 8,
                              padding: '0.75rem 1rem',
                              fontSize: '0.8rem',
                              color: 'rgba(255,255,255,0.7)',
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: '1.5rem',
                            }}>
                              <div>
                                <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.72rem', textTransform: 'uppercase' }}>User Agent</span>
                                <div style={{ marginTop: 2, wordBreak: 'break-all', maxWidth: 500 }}>{log.userAgent}</div>
                              </div>
                              <div>
                                <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.72rem', textTransform: 'uppercase' }}>Metadata</span>
                                <pre style={{ margin: '2px 0 0', fontFamily: 'monospace', fontSize: '0.78rem', color: '#00f2fe' }}>
                                  {JSON.stringify(log.metadata || {}, null, 2)}
                                </pre>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  ))
              }
            </tbody>
          </table>
        </div>

        {/* ── Pagination ── */}
        {!loading && pagination.totalPages > 1 && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: '0.4rem', padding: '1rem 1.5rem',
            borderTop: '1px solid rgba(255,255,255,0.07)',
            flexWrap: 'wrap',
          }}>
            <button className="page-btn" disabled={pagination.page <= 1} onClick={() => fetchLogs(1)}>«</button>
            <button className="page-btn" disabled={pagination.page <= 1} onClick={() => fetchLogs(pagination.page - 1)}>‹</button>

            {Array.from({ length: Math.min(pagination.totalPages, 7) }, (_, i) => {
              let p;
              if (pagination.totalPages <= 7) {
                p = i + 1;
              } else if (pagination.page <= 4) {
                p = i + 1;
              } else if (pagination.page >= pagination.totalPages - 3) {
                p = pagination.totalPages - 6 + i;
              } else {
                p = pagination.page - 3 + i;
              }
              return (
                <button
                  key={p}
                  className={`page-btn${pagination.page === p ? ' active' : ''}`}
                  onClick={() => fetchLogs(p)}
                >
                  {p}
                </button>
              );
            })}

            <button className="page-btn" disabled={pagination.page >= pagination.totalPages} onClick={() => fetchLogs(pagination.page + 1)}>›</button>
            <button className="page-btn" disabled={pagination.page >= pagination.totalPages} onClick={() => fetchLogs(pagination.totalPages)}>»</button>

            <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem', marginLeft: '0.5rem' }}>
              Page {pagination.page} of {pagination.totalPages}
            </span>
          </div>
        )}
      </div>
    </div>
    </AppLayout>
  );
};

export default ActivityLogPage;
